'use client'

import Link from 'next/link'
import {
  Education as GraduationCap,
  ArrowUpRight,
  Group as Users,
  EventSchedule as CalendarClock,
  Wallet,
  Report as ClipboardList,
  CertificateCheck as BadgeCheck,
  Money as Banknote,
  FolderOpen,
  Partnership as HeartHandshake,
  Screen as Monitor,
  Favorite as Heart,
  Star,
  Package,
  Chat as MessageCircleHeart,
  Lifesaver as LifeBuoy,
  Trophy,
  ContainerSoftware as Container,
  Book as BookOpen,
  Devices as MonitorSmartphone,
  Bus,
  Archive,
  Network_3 as Network,
  Badge as Award,
} from '@carbon/icons-react'

import { ChromaText } from '@/components/ui/chroma-text'
import { ModuleSpotlight } from './ModuleSpotlight'

/**
 * The module hub. On desktop it runs as a keynote slide: the module list
 * scrolls through a focus lens on the right while the module on the centre
 * line puts its authored figure up in gradient type on the left. Every row is
 * a real link (click or keyboard), and hover/focus glides it to the centre.
 *
 * Below lg that gives way to a plain scannable grid — a moving focal list
 * doesn't work with thumbs.
 */

// ─── Types ────────────────────────────────────────────────────────────────────
export interface Feature {
  slug: string
  title: string
  tagline: string
  category: string
  iconName: string
  /** Authored per-module figures; the spotlight shows the first one. */
  heroStat?: { value: string; label: string }[]
}

export interface FeaturesHubProps {
  features: Feature[]
  basePath?: string
  heading?: React.ReactNode
  subheading?: string
}

// ─── Icon registry (single source of truth) ───────────────────────────────────
const ICON_REGISTRY = {
  GraduationCap,
  Users,
  CalendarClock,
  Wallet,
  ClipboardList,
  BadgeCheck,
  Banknote,
  FolderOpen,
  HeartHandshake,
  Monitor,
  Heart,
  Star,
  Package,
  MessageCircleHeart,
  LifeBuoy,
  Trophy,
  Container,
  BookOpen,
  MonitorSmartphone,
  Bus,
  Archive,
  Network,
  Medal: Trophy,
  Award,
} as const

function FeatureIcon({
  name,
  className,
  size = 22,
  color,
}: {
  name: string
  className?: string
  size?: number
  color?: string
}) {
  const Icon =
    ICON_REGISTRY[name as keyof typeof ICON_REGISTRY] ?? GraduationCap
  return <Icon size={size} className={className} color={color} aria-hidden />
}

// ─── Main export ──────────────────────────────────────────────────────────────
export function FeaturesHub({
  features,
  basePath = '/schoolerp/features',
  heading,
  subheading,
}: FeaturesHubProps) {
  return (
    <section
      id="features"
      aria-label="Platform features"
      className="bg-background"
    >
      <div className="container mx-auto pb-6 pt-12 sm:pb-8 sm:pt-16">
        {/* ── Header — the site's eyebrow / chroma / muted-intro language ── */}
        <div className="mx-auto max-w-3xl text-center">
          <span className="text-[11px] font-black uppercase tracking-[0.24em] text-foreground">
            Platform Capabilities
          </span>
          <h2 className="font-heading mt-4 text-3xl font-normal leading-tight tracking-tight text-foreground sm:text-4xl md:text-[42px]">
            <ChromaText>
              {heading ?? (
                <>
                  20+ Integrated Modules.{' '}
                  <span className="text-emerald-600 dark:text-primary-active">
                    One Unified Platform.
                  </span>
                </>
              )}
            </ChromaText>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            {subheading ??
              'Every module is purpose-built for schools and deeply interconnected: no integrations to maintain, no information lost between tools.'}
          </p>

          {/* The module index was reachable only from the sitemap: the rows
              below link straight past it to the individual modules. */}
          <Link
            href={basePath}
            className="group mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-emerald-600 dark:text-primary-active"
          >
            Compare all {features.length} modules in one place
            <ArrowUpRight
              size={13}
              className="transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              aria-hidden
            />
          </Link>
        </div>

        {/* ── Desktop: the spotlight slide ── */}
        <div className="mt-8 hidden lg:block">
          <ModuleSpotlight features={features} basePath={basePath} />
        </div>

        {/* ── Below lg: every module on the page, every card a link ── */}
        <div className="mt-10 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:hidden">
          {features.map((f) => (
            <Link
              key={f.slug}
              href={`${basePath}/${f.slug}`}
              className="group flex items-start gap-3.5 rounded-xl border border-border/70 bg-background p-4 sm:p-5"
            >
              <FeatureIcon
                name={f.iconName}
                className="mt-0.5 shrink-0 text-emerald-600 dark:text-primary-active"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground/80">
                      {f.category}
                    </p>
                    <h3 className="mt-1 text-sm font-semibold leading-snug text-foreground sm:text-[15px]">
                      {f.title}
                    </h3>
                  </div>
                  <ArrowUpRight
                    size={14}
                    className="mt-0.5 shrink-0 text-muted-foreground/70 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    aria-hidden
                  />
                </div>
                <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground line-clamp-2 sm:text-[13px]">
                  {f.tagline}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

export default FeaturesHub
